import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { ListingMediaPatchInput } from "@avestate/shared";
import { apiFetch } from "@/lib/api.js";
import { useAuth } from "@/auth/useAuth.js";

const PresignResponse = z.object({
  media_id: z.string().uuid(),
  storage_key: z.string(),
  upload_url: z.string().url(),
  upload_token: z.string(),
  expires_in_seconds: z.number(),
});

const FinalizeResponse = z.object({
  listing_media: z.object({
    id: z.string().uuid(),
    position: z.number(),
    is_cover: z.boolean(),
  }),
  media_asset: z.object({
    id: z.string().uuid(),
    exif_stripped: z.boolean(),
    privacy_scan_status: z.string(),
    privacy_scan_results: z.unknown().nullable(),
  }),
});

const MediaRow = z.object({
  id: z.string().uuid(),
  media_id: z.string().uuid(),
  position: z.number(),
  caption: z.record(z.string()).nullable(),
  alt: z.record(z.string()).nullable(),
  is_cover: z.boolean(),
  asset: z.object({
    id: z.string().uuid(),
    kind: z.string(),
    storage_key: z.string(),
    mime_type: z.string(),
    width: z.number().nullable(),
    height: z.number().nullable(),
    privacy_scan_status: z.string(),
  }),
});
export type MediaRow = z.infer<typeof MediaRow>;

const MediaListResponse = z.object({ media: z.array(MediaRow) });

export function useListingMedia(listingId: string | undefined) {
  const { session } = useAuth();
  const token = session?.access_token;
  return useQuery({
    queryKey: ["media", listingId],
    enabled: !!listingId && !!token,
    queryFn: async (): Promise<MediaRow[]> => {
      const res = await apiFetch(
        `/api/v1/admin/listings/${listingId!}/media`,
        token!,
        MediaListResponse,
      );
      return res.media;
    },
  });
}

export function usePresignAndUpload(listingId: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;

  return useMutation({
    mutationFn: async (file: File) => {
      if (!token) throw new Error("not authenticated");
      // 1. Presign.
      const presign = await apiFetch(`/api/v1/admin/listings/${listingId}/media/presign`, token, PresignResponse, {
        method: "POST",
        body: {
          filename: file.name,
          mime_type: file.type,
          byte_size: file.size,
          kind: file.type.startsWith("video/") ? "video" : "photo",
        },
      });
      // 2. PUT to signed URL.
      const putRes = await fetch(presign.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`upload failed: ${putRes.status}`);
      // 3. Finalize (strip EXIF + privacy scan + listing_media insert).
      const finalized = await apiFetch(
        `/api/v1/admin/listings/${listingId}/media/finalize?media_id=${presign.media_id}`,
        token, FinalizeResponse, { method: "POST", body: { position: 999, is_cover: false } },
      );
      return finalized;
    },
    onSuccess: () => {
      // Media changes move the cover (position 0), so refresh the listing-level
      // caches that render cover_url — otherwise the editor/list preview stays stale.
      void qc.invalidateQueries({ queryKey: ["media", listingId] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}

const MediaPatchResponse = z.object({
  id: z.string().uuid(),
  position: z.number(),
  caption: z.record(z.string()).nullable(),
  alt: z.record(z.string()).nullable(),
  is_cover: z.boolean(),
});

export function usePatchListingMedia(listingId: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: z.infer<typeof ListingMediaPatchInput> }) => {
      if (!token) throw new Error("not authenticated");
      return apiFetch(`/api/v1/admin/listing-media/${id}`, token, MediaPatchResponse, {
        method: "PATCH", body: patch,
      });
    },
    onSuccess: () => {
      // Media changes move the cover (position 0), so refresh the listing-level
      // caches that render cover_url — otherwise the editor/list preview stays stale.
      void qc.invalidateQueries({ queryKey: ["media", listingId] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}

export function useDeleteListingMedia(listingId: string) {
  const qc = useQueryClient();
  const { session } = useAuth();
  const token = session?.access_token;
  return useMutation({
    mutationFn: async (id: string) => {
      if (!token) throw new Error("not authenticated");
      await apiFetch(`/api/v1/admin/listing-media/${id}`, token, z.void(), { method: "DELETE" });
    },
    onSuccess: () => {
      // Media changes move the cover (position 0), so refresh the listing-level
      // caches that render cover_url — otherwise the editor/list preview stays stale.
      void qc.invalidateQueries({ queryKey: ["media", listingId] });
      void qc.invalidateQueries({ queryKey: ["listing", listingId] });
      void qc.invalidateQueries({ queryKey: ["listings"] });
    },
  });
}
